import { useMemo } from "react";

import { Box, Button, Text } from "@chakra-ui/react";

import { format } from "date-fns";
import { IoMdArrowBack } from "react-icons/io";
import { useParams } from "react-router-dom";

import { DataType } from "@/jenesys-types/data/enums";
import { BankStatementUpload } from "@/jenesys-types/jenesys-entities/config";
import { EntityRouteListIds } from "@/jenesys-types/list";

import { CrudListContextProvider } from "@/components/common/crud/CrudList/CrudListContext";
import { CrudListTable } from "@/components/common/crud/CrudList/CrudListTable";
import { NamedLink } from "@/components/common/navigation/NamedLink";
import { ColumnDefinition } from "@/components/common/table/types";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";

export const ReconStatementUploads = () => {
  const { clientId, reconciliationId } = useParams();
  const namedNavigate = useNamedNavigate();

  const columns = useMemo<Array<ColumnDefinition<BankStatementUpload>>>(
    () => [
      {
        id: "DATE",
        header: "DATE UPLOADED",
        accessor: ({ created_at }) =>
          created_at ? format(created_at, "dd-MM-yyyy") : "—"
      },
      {
        id: "NAME",
        header: "BANK STATEMENT",
        accessor: ({ filename }) => filename || "—"
      },
      {
        id: "STATUS",
        header: "STATUS",
        accessor: ({ status }) => status || "—"
      },
      {
        id: "ACTION",
        header: "ACTION",
        accessor: ({ status, id }) =>
          (status === "EXTRACTED" || status === "CONFIRMED") && (
            <Button
              name="bankStatementUploads.edit"
              as={NamedLink}
              params={{
                bankStatementUploadsId: id,
                clientId,
                reconciliationId
              }}
              variant="link"
              colorScheme="blue"
              size="sm"
            >
              View
            </Button>
          )
      }
    ],
    [clientId, reconciliationId]
  );

  return (
    <CrudListContextProvider<BankStatementUpload>
      dataType={DataType.BankStatementUploads}
      listId={EntityRouteListIds.BankStatementUploads}
      pathParams={{
        clientId,
        accountId: reconciliationId
      }}
    >
      <Box mt={-24} px={16}>
        <Button
          onClick={() =>
            namedNavigate("bankStatement.list", {
              clientId,
              reconciliationId
            })
          }
          color="#5C343F"
          leftIcon={<IoMdArrowBack />}
          size="sm"
          variant="link"
        >
          Back
        </Button>
        <Text mt={12} fontSize={14} fontWeight={500}>
          Track the status of your uploaded bank statements. Once extraction is
          complete, you can view and edit the transactions.
        </Text>

        <Box bg="white" fontSize={14} mt={8}>
          <CrudListTable columns={columns} />
        </Box>
      </Box>
    </CrudListContextProvider>
  );
};
